import { UntypedFormGroup } from '@angular/forms';

// custom validator to check that two fields match
export function mustMatch(controlName: string, matchingControlName: string) {
  return (formGroup: UntypedFormGroup) => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];

    if (matchingControl.errors && !matchingControl.errors['mustMatch']) {
      // return if another validator has already found an error on the matchingControl
      return;
    }

    // set error on matchingControl if validation fails
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mustMatch: true });
    } else {
      matchingControl.setErrors(null);
    }
  };
}

export function convertDate2String(date: Date, keepTime: boolean = false) {
  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - offset * 60 * 1000);
  if (keepTime) {
    return date.toISOString();
  } else {
    return date.toISOString().split('T')[0];
  }
}

export function convertDateTime2Date(date: string) {
  return date.slice(0, 10);
}

export function hasValue<Type>(value: Type): boolean {
  return value !== null && value !== undefined;
}

export function hasValueAndNotEmpty(value: any) {
  return value !== null && value !== undefined && value !== '';
}

export function isEmptyArray(value: any) {
  return Array.isArray(value) && value?.length == 0;
}

export type RequireOne<T, K extends keyof T> = {
  [X in Exclude<keyof T, K>]?: T[X];
} & {
  [P in K]-?: T[P];
};

export function getDirtyValues(form: any) {
  let dirtyValues = {};
  Object.keys(form.controls).forEach(key => {
    let currentControl = form.controls[key];
    if (currentControl.dirty) {
      if (currentControl.controls) dirtyValues[key] = this.getDirtyValues(currentControl);
      else dirtyValues[key] = currentControl.value;
    }
  });
  for (const [key, value] of Object.entries(dirtyValues)) {
    if (value instanceof Date) dirtyValues[key] = convertDate2String(value, true);
  }
  return dirtyValues;
}

export function getLink(url: string) {
  if (hasValue(url)) {
    if (url.includes('://')) {
      return url;
    } else {
      return `https://` + url;
    }
  }
}

export function htmlToPlainText(html) {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = html;

  // Replace block-level elements with their text content followed by a newline
  tempElement.querySelectorAll('p, ul, ol, li').forEach(element => {
    const text = element.textContent || '';
    element.replaceWith(document.createTextNode(text + '\n'));
  });
  return tempElement.textContent || tempElement.innerText || '';
}

/** Returns the first Monday of the given month at 00:00 local time. */
export function firstMondayOfMonth(year: number, month: number): Date {
  const first = new Date(year, month, 1);
  const daysUntilMonday = (8 - first.getDay()) % 7;
  first.setDate(1 + daysUntilMonday);
  return first;
}

/** True if `now` is within the 5-day and ending on the upcoming
 *  first Monday of the month. */
export function isNetworkingRoundApproaching(now: Date = new Date()): boolean {
  const today = startOfDay(now);

  let nextRound = firstMondayOfMonth(today.getFullYear(), today.getMonth());
  if (today > nextRound) {
    nextRound = firstMondayOfMonth(today.getFullYear(), today.getMonth() + 1);
  }

  const windowStart = new Date(nextRound);
  windowStart.setDate(nextRound.getDate() - 5);

  return today >= windowStart && today < nextRound;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Strips HTML to plain text with stable list markers: "1. " / "2. " for ol, "• " for ul. */
export function htmlToPlainTextWithListMarkers(html: string): string {
  const body = new DOMParser().parseFromString(html, 'text/html').body;

  const collapseWs = (s: string) => s.replace(/\s+/g, ' ').trim();

  const olListStart = (list: HTMLElement): number => {
    const start = list.getAttribute('start');
    if (start == null || start === '') {
      return 1;
    }
    const n = parseInt(start, 10);
    return Number.isFinite(n) ? n : 1;
  };

  const formatList = (list: HTMLElement): string => {
    const ordered = list.tagName === 'OL';
    const base = ordered ? olListStart(list) : 0;
    const items = Array.from(list.children).filter((c): c is HTMLLIElement => c.tagName === 'LI');
    return items
      .map((li, index) => {
        const prefix = ordered ? `${base + index}. ` : '• ';
        const textBits: string[] = [];
        const nestedLists: string[] = [];
        li.childNodes.forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            const t = collapseWs(child.textContent ?? '');
            if (t) {
              textBits.push(t);
            }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const el = child as HTMLElement;
            if (el.tagName === 'OL' || el.tagName === 'UL') {
              nestedLists.push(formatList(el));
            } else if (el.tagName === 'BR') {
              textBits.push('\n');
            } else {
              const t = collapseWs(el.textContent ?? '');
              if (t) {
                textBits.push(t);
              }
            }
          }
        });
        const line =
          prefix +
          textBits
            .join(' ')
            .replace(/[ \t]*\n[ \t]*/g, '\n')
            .trim();
        return nestedLists.length ? [line, ...nestedLists].join('\n') : line;
      })
      .join('\n');
  };

  const blocks: string[] = [];

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = collapseWs(node.textContent ?? '');
      if (t) {
        blocks.push(t);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName === 'OL' || el.tagName === 'UL') {
        blocks.push(formatList(el));
      } else if (el.tagName === 'P') {
        const t = collapseWs(el.textContent ?? '');
        if (t) {
          blocks.push(t);
        }
      } else if (el.tagName === 'BR') {
        blocks.push('');
      } else {
        el.childNodes.forEach(walk);
      }
    }
  };

  body.childNodes.forEach(walk);
  return blocks
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
